import { c as useScreenContext, o as useTerminalSize, t as getOutputStore } from "./store-key-fUtc4MNq.js";
import { i as formatCheck, n as formatFinding, t as formatSummary } from "./tally-CQivzeuB.js";
import pc from "picocolors";
import { match } from "ts-pattern";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Text, measureElement, useInput } from "ink";
import defaultFigures from "figures";
//#region src/ui/layout/scroll-area.tsx
/**
* A vertically scrollable container that clips content to a fixed height
* and auto-scrolls to keep the active item in view.
*
* Uses Ink's `overflow="hidden"` for clipping and slices children to the
* visible window. Optionally renders a scroll indicator showing relative
* position within the list.
*
* @param props - The scroll area props.
* @returns A rendered scroll area element.
*/
function ScrollArea({ children, height, activeIndex, itemCount, showIndicator = false, scrollOffset: controlledOffset, onScrollChange }) {
	const childArray = flattenChildren(children);
	const totalItems = itemCount ?? childArray.length;
	const isControlled = controlledOffset !== void 0;
	const [internalOffset, setInternalOffset] = useState(0);
	const offset = resolveOffset(isControlled, controlledOffset, internalOffset);
	useEffect(() => {
		if (activeIndex === void 0) return;
		const newOffset = clampOffset({
			activeIndex,
			currentOffset: offset,
			height,
			totalItems
		});
		if (newOffset === offset) return;
		if (!isControlled) setInternalOffset(newOffset);
		if (onScrollChange) onScrollChange(newOffset);
	}, [
		activeIndex,
		height,
		totalItems,
		offset,
		isControlled,
		onScrollChange
	]);
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "row",
		children: [/* @__PURE__ */ jsx(Box, {
			flexDirection: "column",
			overflow: "hidden",
			height,
			children: childArray.slice(offset, offset + height)
		}), match(showIndicator && totalItems > height).with(true, () => /* @__PURE__ */ jsx(ScrollIndicator, {
			offset,
			height,
			totalItems
		})).with(false, () => null).exhaustive()]
	});
}
/**
* Render a vertical scroll indicator showing the current viewport
* position within the full list.
*
* @private
* @param props - The indicator props.
* @returns A rendered scroll indicator column.
*/
function ScrollIndicator({ offset, height, totalItems }) {
	const thumbSize = Math.max(1, Math.round(height / totalItems * height));
	const maxThumbOffset = height - thumbSize;
	const thumbOffset = computeThumbOffset(totalItems - height, offset, maxThumbOffset);
	return /* @__PURE__ */ jsx(Box, {
		flexDirection: "column",
		marginLeft: 1,
		children: Array.from({ length: height }, (_, i) => {
			const isThumb = i >= thumbOffset && i < thumbOffset + thumbSize;
			return /* @__PURE__ */ jsx(Text, {
				dimColor: !isThumb,
				children: match(isThumb).with(true, () => "┃").with(false, () => "│").exhaustive()
			}, i);
		})
	});
}
/**
* Resolve the effective scroll offset, preferring the controlled value
* when the component is in controlled mode.
*
* @private
* @param isControlled - Whether the component is in controlled mode.
* @param controlledOffset - The externally provided offset.
* @param internalOffset - The internally managed offset.
* @returns The resolved offset.
*/
function resolveOffset(isControlled, controlledOffset, internalOffset) {
	if (isControlled && controlledOffset !== void 0) return controlledOffset;
	return internalOffset;
}
/**
* Compute the thumb position within the scroll indicator track.
* Returns 0 when the scroll range is zero (no scrolling possible).
*
* @private
* @param scrollRange - Total scrollable range.
* @param offset - Current scroll offset.
* @param maxThumbOffset - Maximum thumb position.
* @returns The thumb offset position.
*/
function computeThumbOffset(scrollRange, offset, maxThumbOffset) {
	if (scrollRange <= 0) return 0;
	return Math.round(offset / scrollRange * maxThumbOffset);
}
/**
* Compute a new scroll offset that keeps `activeIndex` within the
* visible viewport. Returns `currentOffset` unchanged when the
* active item is already visible.
*
* @private
* @param options - The clamp calculation inputs.
* @returns The clamped scroll offset.
*/
function clampOffset({ activeIndex, currentOffset, height, totalItems }) {
	const maxOffset = Math.max(0, totalItems - height);
	if (activeIndex < currentOffset) return Math.max(0, activeIndex);
	if (activeIndex >= currentOffset + height) return Math.min(maxOffset, activeIndex - height + 1);
	return Math.min(currentOffset, maxOffset);
}
/**
* Flatten React children into an array of ReactNode elements.
* Handles arrays, fragments, and single children uniformly.
*
* @private
* @param children - The React children to flatten.
* @returns A flat array of child nodes.
*/
function flattenChildren(children) {
	if (children === null || children === void 0) return [];
	if (Array.isArray(children)) return children.flat();
	return [children];
}
//#endregion
//#region src/ui/theme.ts
/**
* Color palette used by kidd UI components.
*/
const colors = Object.freeze({
	primary: "cyan",
	success: "green",
	error: "red",
	warning: "yellow",
	info: "blue"
});
/**
* Resolve the theme color for a given variant.
*
* @param variant - The display variant.
* @returns The color string from the theme palette.
*/
function resolveVariantColor(variant) {
	return match(variant).with("info", () => colors.info).with("success", () => colors.success).with("error", () => colors.error).with("warning", () => colors.warning).exhaustive();
}
/**
* Symbol set used by kidd UI components for indicators and status icons.
*/
const symbols = Object.freeze({
	radioOn: defaultFigures.radioOn,
	radioOff: defaultFigures.radioOff,
	checkboxOn: defaultFigures.checkboxOn,
	checkboxOff: defaultFigures.checkboxOff,
	pointer: defaultFigures.pointer,
	tick: defaultFigures.tick,
	cross: defaultFigures.cross,
	warning: defaultFigures.warning,
	info: defaultFigures.info,
	circle: defaultFigures.circle,
	line: defaultFigures.line,
	ellipsis: defaultFigures.ellipsis
});
//#endregion
//#region src/ui/prompts/cursor-value.tsx
/**
* Render a string with a visible cursor at the specified position.
*
* The character at the cursor position is rendered with inverse styling.
* If the cursor is at the end, an inverse space is appended.
*
* @param props - The cursor value props.
* @returns A rendered value element with cursor.
*/
function CursorValue({ value, cursor, disabled }) {
	const beforeCursor = value.slice(0, cursor);
	const atCursor = value[cursor];
	const afterCursor = value.slice(cursor + 1);
	return /* @__PURE__ */ jsxs(Text, {
		dimColor: disabled,
		children: [
			beforeCursor,
			/* @__PURE__ */ jsx(Text, {
				inverse: true,
				children: match(atCursor).with(void 0, () => " ").otherwise((ch) => ch)
			}),
			afterCursor
		]
	});
}
//#endregion
//#region src/ui/prompts/navigation.ts
/**
* Resolve a keyboard event to a navigation direction.
*
* @param key - The Ink key object with arrow properties.
* @returns The resolved direction.
*/
function resolveDirection(key) {
	return match(key).with({ upArrow: true }, () => "up").with({ downArrow: true }, () => "down").otherwise(() => "none");
}
/**
* Compute the next focusable option index, skipping disabled options.
*
* @param opts - The navigation options.
* @returns The next valid focus index.
*/
function resolveNextFocusIndex({ options, currentIndex, direction }) {
	if (direction === "none") return currentIndex;
	return findNextEnabledIndex(options, currentIndex, match(direction).with("up", () => -1).with("down", () => 1).exhaustive());
}
/**
* Find the first non-disabled option index.
*
* @param options - The option list.
* @returns The first enabled index, or 0 if none found.
*/
function resolveFirstEnabledIndex(options) {
	const index = options.findIndex((o) => o.disabled !== true);
	return Math.max(0, index);
}
/**
* Resolve the initial focus index from a default value. Falls back to
* the first non-disabled option if no match is found.
*
* @param opts - The resolution options.
* @returns The initial focus index.
*/
function resolveInitialIndex({ options, defaultValue }) {
	if (defaultValue !== void 0) {
		const matchIndex = options.findIndex((o) => o.value === defaultValue);
		if (matchIndex !== -1) return matchIndex;
	}
	return resolveFirstEnabledIndex(options);
}
/**
* Walk through options in the given step direction to find the next
* non-disabled index. Returns the current index if no enabled option
* is found.
*
* @private
* @param options - The option list.
* @param startIndex - The index to start searching from.
* @param step - The step direction (-1 or 1).
* @returns The next enabled index, or the start index if none found.
*/
function findNextEnabledIndex(options, startIndex, step) {
	const count = options.length;
	return Array.from({ length: count - 1 }, (_, i) => {
		return ((startIndex + (i + 1) * step) % count + count) % count;
	}).find((i) => {
		const opt = options[i];
		return opt !== void 0 && opt.disabled !== true;
	}) ?? startIndex;
}
//#endregion
//#region src/ui/prompts/option-row.tsx
/**
* Render a single option row with pointer, indicator, label, and hint.
*
* Shared by Select, MultiSelect, and similar prompt components to ensure
* consistent styling and keyboard focus behavior.
*
* @param props - The option row props.
* @returns A rendered option row element.
*/
function OptionRow({ option, indicator, isFocused, isSelected, disabled }) {
	const isOptionDisabled = option.disabled === true || disabled;
	return /* @__PURE__ */ jsxs(Box, { children: [
		/* @__PURE__ */ jsx(Text, {
			dimColor: !isFocused,
			children: match(isFocused).with(true, () => `${symbols.pointer} `).with(false, () => "  ").exhaustive()
		}),
		/* @__PURE__ */ jsx(Text, {
			color: match({
				isOptionDisabled,
				isSelected,
				isFocused
			}).with({ isOptionDisabled: true }, () => "gray").with({ isSelected: true }, () => colors.primary).with({ isFocused: true }, () => colors.primary).otherwise(() => void 0),
			children: indicator
		}),
		/* @__PURE__ */ jsx(Text, { children: " " }),
		/* @__PURE__ */ jsxs(Text, {
			color: match({
				isOptionDisabled,
				isFocused
			}).with({ isOptionDisabled: true }, () => "gray").with({ isFocused: true }, () => colors.primary).otherwise(() => void 0),
			children: [option.label, match(isOptionDisabled && !pc.isColorSupported).with(true, () => " (disabled)").with(false, () => "").exhaustive()]
		}),
		match(option.hint).with(void 0, () => null).otherwise((hint) => /* @__PURE__ */ jsx(Text, {
			color: match(isOptionDisabled).with(true, () => "gray").with(false, () => void 0).exhaustive(),
			dimColor: true,
			children: `  ${hint}`
		}))
	] });
}
//#endregion
//#region src/ui/prompts/confirm.tsx
/**
* A boolean confirm prompt with toggle-style keyboard navigation.
*
* Renders two choices side by side. The active choice is styled with
* cyan color and underline. Users toggle with left/right arrows or
* y/n keys and submit with Enter.
*
* **Keyboard shortcuts:**
* - Left/Right arrows — toggle between choices
* - y — select the affirmative choice
* - n — select the negative choice
* - Enter — submit the current value
*
* @param props - The confirm component props.
* @returns A rendered confirm element.
*/
function Confirm({ active = "Yes", inactive = "No", defaultValue = true, onSubmit, focused = true, disabled = false }) {
	const [value, setValue] = useState(defaultValue);
	useInput((input, key) => {
		if (key.return) {
			if (onSubmit) onSubmit(value);
			return;
		}
		if (key.leftArrow || key.rightArrow) {
			setValue(!value);
			return;
		}
		if (input === "y" || input === "Y") {
			setValue(true);
			return;
		}
		if (input === "n" || input === "N") setValue(false);
	}, { isActive: focused && !disabled });
	return /* @__PURE__ */ jsxs(Box, {
		gap: 1,
		children: [
			/* @__PURE__ */ jsx(ConfirmChoice, {
				label: active,
				isActive: value,
				disabled
			}),
			/* @__PURE__ */ jsx(Text, {
				dimColor: true,
				children: "/"
			}),
			/* @__PURE__ */ jsx(ConfirmChoice, {
				label: inactive,
				isActive: !value,
				disabled
			})
		]
	});
}
/**
* Render a single confirm choice with active/inactive styling.
*
* @private
* @param props - The choice props.
* @returns A rendered choice element.
*/
function ConfirmChoice({ label, isActive, disabled }) {
	return /* @__PURE__ */ jsx(Text, {
		color: match({
			isActive,
			disabled
		}).with({ disabled: true }, () => void 0).with({ isActive: true }, () => colors.primary).otherwise(() => void 0),
		dimColor: !isActive || disabled,
		underline: isActive && !disabled,
		children: label
	});
}
//#endregion
//#region src/ui/display/error-message.tsx
/**
* Render a validation error message in red, or nothing when absent.
*
* @param props - The error message props.
* @returns A rendered error text element, or `null`.
*/
function ErrorMessage({ message }) {
	return match(message).with(void 0, () => null).otherwise((msg) => /* @__PURE__ */ jsx(Text, {
		color: colors.error,
		children: msg
	}));
}
//#endregion
//#region src/ui/prompts/multi-select.tsx
/**
* A multi-value select prompt with checkbox indicators and keyboard navigation.
*
* Renders a vertical list of options with checkbox-style indicators. Users
* toggle selections with Space and submit with Enter. Disabled options are
* rendered dimmed with strikethrough and cannot be toggled.
*
* **Keyboard shortcuts:**
* - Up/Down arrows — navigate between enabled options
* - Space — toggle the focused option
* - Enter — submit the current selection
*
* @typeParam TValue - The type of each option's value.
* @param props - The multi-select component props.
* @returns A rendered multi-select element.
*/
function MultiSelect({ options, defaultValue, maxVisible = 5, required = false, onChange, onSubmit, focused = true, disabled = false }) {
	const initialSelected = resolveInitialSelected(options, defaultValue);
	const [focusedIndex, setFocusedIndex] = useState(resolveFirstEnabledIndex(options));
	const [selectedIndices, setSelectedIndices] = useState(initialSelected);
	const [validationError, setValidationError] = useState(void 0);
	useInput((input, key) => {
		if (key.return) {
			if (required && selectedIndices.size === 0) {
				setValidationError("At least one option must be selected");
				return;
			}
			setValidationError(void 0);
			const selectedValues = resolveSelectedValues(options, selectedIndices);
			if (onSubmit) onSubmit(selectedValues);
			return;
		}
		if (input === " ") {
			const option = options[focusedIndex];
			if (option && option.disabled !== true) {
				const nextSelected = toggleIndex(selectedIndices, focusedIndex);
				setSelectedIndices(nextSelected);
				setValidationError(void 0);
				if (onChange) onChange(resolveSelectedValues(options, nextSelected));
			}
			return;
		}
		const nextIndex = resolveNextFocusIndex({
			options,
			currentIndex: focusedIndex,
			direction: resolveDirection(key)
		});
		if (nextIndex !== focusedIndex) setFocusedIndex(nextIndex);
	}, { isActive: focused && !disabled });
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		children: [/* @__PURE__ */ jsx(ScrollArea, {
			height: Math.min(maxVisible, options.length),
			activeIndex: focusedIndex,
			children: options.map((option, index) => {
				const isSelected = selectedIndices.has(index);
				return /* @__PURE__ */ jsx(OptionRow, {
					option,
					indicator: match(isSelected).with(true, () => symbols.checkboxOn).with(false, () => symbols.checkboxOff).exhaustive(),
					isFocused: index === focusedIndex,
					isSelected,
					disabled
				}, String(option.value));
			})
		}), /* @__PURE__ */ jsx(ErrorMessage, { message: validationError })]
	});
}
/**
* Toggle an index in a readonly set, returning a new set.
*
* @private
* @param set - The current selection set.
* @param index - The index to toggle.
* @returns A new set with the index toggled.
*/
function toggleIndex(set, index) {
	const next = new Set(set);
	if (next.has(index)) next.delete(index);
	else next.add(index);
	return next;
}
/**
* Resolve the initially selected indices from default values.
*
* @private
* @param options - The option list.
* @param defaultValue - The default selected values.
* @returns A set of selected indices.
*/
function resolveInitialSelected(options, defaultValue) {
	if (defaultValue === void 0) return /* @__PURE__ */ new Set();
	const indices = options.map((option, index) => ({
		option,
		index
	})).filter(({ option }) => defaultValue.includes(option.value)).map(({ index }) => index);
	return new Set(indices);
}
/**
* Extract the values from selected indices.
*
* @private
* @param options - The option list.
* @param selectedIndices - The set of selected indices.
* @returns An array of selected values.
*/
function resolveSelectedValues(options, selectedIndices) {
	return options.filter((_, index) => selectedIndices.has(index)).map((option) => option.value);
}
//#endregion
//#region src/ui/prompts/input-state.ts
/**
* Compute the next input state from a keyboard event.
*
* Handles cursor movement (left/right, home/end), character deletion
* (backspace, Ctrl+D for forward-delete), and character insertion.
*
* Backspace removes the character before the cursor.
* Forward-delete uses Ctrl+D.
*
* @param options - The state resolution options.
* @returns The next input state.
*/
function resolveNextState({ state, input, key }) {
	if (key.leftArrow) return {
		...state,
		cursor: Math.max(0, state.cursor - 1)
	};
	if (key.rightArrow) return {
		...state,
		cursor: Math.min(state.value.length, state.cursor + 1)
	};
	if (key.ctrl && input === "a") return {
		...state,
		cursor: 0
	};
	if (key.ctrl && input === "e") return {
		...state,
		cursor: state.value.length
	};
	if (key.ctrl && input === "d") {
		if (state.cursor >= state.value.length) return state;
		const nextValue = state.value.slice(0, state.cursor) + state.value.slice(state.cursor + 1);
		return {
			...state,
			value: nextValue,
			error: void 0
		};
	}
	if (key.backspace) {
		if (state.cursor === 0) return state;
		const nextValue = state.value.slice(0, state.cursor - 1) + state.value.slice(state.cursor);
		return {
			...state,
			value: nextValue,
			cursor: state.cursor - 1,
			error: void 0
		};
	}
	if (key.ctrl || key.meta) return state;
	if (input.length === 0 || key.return) return state;
	const nextValue = state.value.slice(0, state.cursor) + input + state.value.slice(state.cursor);
	return {
		...state,
		value: nextValue,
		cursor: state.cursor + input.length,
		error: void 0
	};
}
//#endregion
//#region src/ui/prompts/select.tsx
/**
* A single-value select prompt with keyboard navigation and scroll support.
*
* Renders a vertical list of options with radio-style indicators. The focused
* option is highlighted with a pointer and cyan coloring. Disabled options are
* rendered dimmed with strikethrough and are skipped during navigation.
*
* **Keyboard shortcuts:**
* - Up/Down arrows — navigate between enabled options
* - Enter — submit the focused option
*
* @typeParam TValue - The type of each option's value.
* @param props - The select component props.
* @returns A rendered select element.
*/
function Select({ options, defaultValue, maxVisible = 5, onChange, onSubmit, focused = true, disabled = false }) {
	const initialIndex = resolveInitialIndex({
		options,
		defaultValue
	});
	const [focusedIndex, setFocusedIndex] = useState(initialIndex);
	const [selectedIndex, setSelectedIndex] = useState(initialIndex);
	useInput((_input, key) => {
		if (key.return) {
			const option = options[focusedIndex];
			if (option && !option.disabled) {
				setSelectedIndex(focusedIndex);
				if (onSubmit) onSubmit(option.value);
			}
			return;
		}
		const nextIndex = resolveNextFocusIndex({
			options,
			currentIndex: focusedIndex,
			direction: resolveDirection(key)
		});
		if (nextIndex !== focusedIndex) {
			setFocusedIndex(nextIndex);
			const nextOption = options[nextIndex];
			if (nextOption && onChange) onChange(nextOption.value);
		}
	}, { isActive: focused && !disabled });
	return /* @__PURE__ */ jsx(ScrollArea, {
		height: Math.min(maxVisible, options.length),
		activeIndex: focusedIndex,
		children: options.map((option, index) => {
			const isSelected = index === selectedIndex;
			return /* @__PURE__ */ jsx(OptionRow, {
				option,
				indicator: match(isSelected).with(true, () => symbols.radioOn).with(false, () => symbols.radioOff).exhaustive(),
				isFocused: index === focusedIndex,
				isSelected,
				disabled
			}, String(option.value));
		})
	});
}
//#endregion
//#region src/ui/prompts/text-input.tsx
/**
* A single-line text input prompt with cursor movement and validation.
*
* Renders the current value with a visible cursor (inverse character).
* Shows placeholder text when the input is empty. Validation errors
* appear below the input in red.
*
* **Keyboard shortcuts:**
* - Left/Right arrows — move cursor
* - Home (Ctrl+A) / End (Ctrl+E) — jump to start/end
* - Backspace — delete character before cursor
* - Delete (Ctrl+D) — delete character at cursor
* - Enter — validate and submit
*
* @param props - The text input component props.
* @returns A rendered text input element.
*/
function TextInput({ placeholder, defaultValue = "", validate, onChange, onSubmit, focused = true, disabled = false }) {
	const [state, setState] = useState({
		value: defaultValue,
		cursor: defaultValue.length,
		error: void 0
	});
	useInput((input, key) => {
		if (key.return) {
			const validationError = match(validate).with(void 0, () => void 0).otherwise((fn) => fn(state.value));
			if (validationError) {
				setState({
					...state,
					error: validationError
				});
				return;
			}
			setState({
				...state,
				error: void 0
			});
			if (onSubmit) onSubmit(state.value);
			return;
		}
		const nextState = resolveNextState({
			state,
			input,
			key
		});
		if (nextState.value !== state.value || nextState.cursor !== state.cursor) {
			setState(nextState);
			if (nextState.value !== state.value && onChange) onChange(nextState.value);
		}
	}, { isActive: focused && !disabled });
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		children: [match(state.value.length === 0 && placeholder !== void 0).with(true, () => /* @__PURE__ */ jsx(Text, {
			dimColor: true,
			children: placeholder
		})).with(false, () => /* @__PURE__ */ jsx(CursorValue, {
			value: state.value,
			cursor: state.cursor,
			disabled
		})).exhaustive(), /* @__PURE__ */ jsx(ErrorMessage, { message: state.error })]
	});
}
//#endregion
//#region src/ui/display/spinner.tsx
/**
* Built-in spinner definitions.
*
* @private
*/
const spinners = Object.freeze({
	dots: {
		interval: 80,
		frames: Object.freeze([
			"⠋",
			"⠙",
			"⠹",
			"⠸",
			"⠼",
			"⠴",
			"⠦",
			"⠧",
			"⠇",
			"⠏"
		])
	},
	line: {
		interval: 130,
		frames: Object.freeze([
			"-",
			"\\",
			"|",
			"/"
		])
	},
	arc: {
		interval: 100,
		frames: Object.freeze([
			"◜",
			"◠",
			"◝",
			"◞",
			"◡",
			"◟"
		])
	},
	bouncingBar: {
		interval: 80,
		frames: Object.freeze([
			"[    ]",
			"[=   ]",
			"[==  ]",
			"[=== ]",
			"[ ===]",
			"[  ==]",
			"[   =]",
			"[    ]",
			"[   =]",
			"[  ==]",
			"[ ===]",
			"[====]",
			"[=== ]",
			"[==  ]",
			"[=   ]"
		])
	}
});
/**
* An animated terminal spinner that cycles through frames.
*
* When `isActive` is `false`, the component renders nothing. The spinner
* frame is colored cyan and the label is rendered as plain text beside it.
*
* @param props - The spinner props.
* @returns A rendered spinner element, or `null` when inactive.
*/
function Spinner({ label, isActive = true, type = "dots" }) {
	const spinner = spinners[type];
	const [frameIndex, setFrameIndex] = useState(0);
	useEffect(() => {
		setFrameIndex(0);
		if (!isActive) return;
		const timer = setInterval(() => {
			setFrameIndex((prev) => (prev + 1) % spinner.frames.length);
		}, spinner.interval);
		return () => {
			clearInterval(timer);
		};
	}, [isActive, spinner]);
	return match(isActive).with(false, () => null).with(true, () => /* @__PURE__ */ jsxs(Text, { children: [/* @__PURE__ */ jsx(Text, {
		color: colors.primary,
		children: spinner.frames[frameIndex]
	}), match(label).with(void 0, () => null).otherwise((text) => /* @__PURE__ */ jsx(Text, { children: ` ${text}` }))] })).exhaustive();
}
//#endregion
//#region src/ui/layout/tabs.tsx
/**
* Horizontal tab bar with keyboard navigation and content switching.
*
* Renders a row of tab labels at the top, with the active tab highlighted
* in cyan and underlined. The corresponding tab content is rendered below.
* Supports both controlled and uncontrolled modes via `activeTab` and
* `onTabChange`.
*
* **Keyboard shortcuts** (when `isFocused` is `true`):
* - Tab key — cycle to next tab (wraps around)
* - Left/Right arrows — move between tabs
*
* @param props - The tabs component props.
* @returns A rendered tabs element.
*/
function Tabs({ tabs, isFocused, activeTab, onTabChange }) {
	const isControlled = activeTab !== void 0;
	const [internalIndex, setInternalIndex] = useState(0);
	const currentIndex = clampIndex(match(isControlled).with(true, () => activeTab ?? 0).with(false, () => internalIndex).exhaustive(), tabs.length);
	useInput((_input, key) => {
		if (tabs.length === 0) return;
		const nextIndex = resolveNextIndex({
			key,
			currentIndex,
			tabCount: tabs.length
		});
		if (nextIndex === currentIndex) return;
		if (!isControlled) setInternalIndex(nextIndex);
		if (onTabChange) onTabChange(nextIndex);
	}, { isActive: isFocused });
	if (tabs.length === 0) return /* @__PURE__ */ jsx(Box, {});
	const activeContent = tabs[currentIndex];
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		flexGrow: 1,
		children: [/* @__PURE__ */ jsx(TabBar, {
			tabs,
			activeIndex: currentIndex,
			isFocused
		}), /* @__PURE__ */ jsx(Box, {
			flexDirection: "column",
			flexGrow: 1,
			children: match(activeContent).with(void 0, () => null).otherwise((tab) => tab.content)
		})]
	});
}
/**
* Render the horizontal tab label row with a navigation hint.
*
* @private
* @param props - The tab bar props.
* @returns A rendered tab bar element.
*/
function TabBar({ tabs, activeIndex, isFocused }) {
	return /* @__PURE__ */ jsx(Box, {
		flexDirection: "column",
		children: /* @__PURE__ */ jsxs(Box, {
			gap: 2,
			children: [tabs.map((tab, index) => {
				const isActive = index === activeIndex;
				return /* @__PURE__ */ jsx(TabLabel, {
					label: tab.label,
					isActive,
					isFocused
				}, tab.label);
			}), match(isFocused).with(true, () => /* @__PURE__ */ jsx(Text, {
				dimColor: true,
				children: "| tab/←→"
			})).with(false, () => null).exhaustive()]
		})
	});
}
/**
* Render a single tab label with active styling and underline indicator.
*
* @private
* @param props - The tab label props.
* @returns A rendered tab label element.
*/
function TabLabel({ label, isActive, isFocused }) {
	const color = resolveTabColor(isActive, isFocused);
	return /* @__PURE__ */ jsx(Text, {
		dimColor: !isActive,
		color,
		bold: isActive,
		underline: isActive,
		children: label
	});
}
/**
* Resolve the color for a tab label based on its active and focus state.
*
* @private
* @param isActive - Whether this tab is the active tab.
* @param isFocused - Whether the tabs component is focused.
* @returns The color string, or undefined for default.
*/
function resolveTabColor(isActive, isFocused) {
	return match({
		isActive,
		isFocused
	}).with({
		isActive: true,
		isFocused: true
	}, () => colors.primary).with({
		isActive: true,
		isFocused: false
	}, () => "white").otherwise(() => void 0);
}
/**
* Compute the next tab index from a keyboard input event. Supports
* left/right arrows for directional movement and Tab to cycle forward
* (wrapping from last to first).
*
* @private
* @param options - The input resolution options.
* @returns The next tab index.
*/
function resolveNextIndex({ key, currentIndex, tabCount }) {
	if (key.tab) return (currentIndex + 1) % tabCount;
	if (key.leftArrow) return Math.max(0, currentIndex - 1);
	if (key.rightArrow) return Math.min(tabCount - 1, currentIndex + 1);
	return currentIndex;
}
/**
* Clamp an index to valid bounds for the given tab count.
*
* @private
* @param index - The index to clamp.
* @param count - The total number of tabs.
* @returns The clamped index.
*/
function clampIndex(index, count) {
	if (count === 0) return 0;
	return Math.max(0, Math.min(count - 1, index));
}
//#endregion
//#region src/ui/layout/use-size.tsx
/**
* Measure the computed dimensions of a `<Box>` element, or fall back to
* terminal dimensions when no ref is provided.
*
* Attaches to a `<Box ref={ref}>` and reads the Yoga-computed width and
* height after each render. When `ref` is omitted or its `.current` is
* `null`, returns the terminal's column and row count instead — behaving
* like a "window size" hook.
*
* Uses a render counter to ensure measurement runs after the ref is
* populated on the first mount and after layout changes.
*
* @example
* ```tsx
* // Measure a specific element
* const ref = useRef<DOMElement>(null)
* const { width, height } = useSize(ref)
* return <Box ref={ref} flexGrow={1}>...</Box>
*
* // Fall back to terminal size
* const { width, height } = useSize()
* ```
*
* @param ref - Optional ref to a `<Box>` element. When omitted, returns
*   terminal dimensions.
* @returns The measured width and height.
*/
function useSize(ref) {
	const terminal = useTerminalSize();
	const [size, setSize] = useState(() => resolveInitialSize(ref, terminal));
	const [tick, bump] = useReducer(incrementTick, 0);
	useEffect(() => {
		bump();
	}, []);
	useEffect(() => {
		if (ref === void 0) {
			setSize((prev) => updateIfChanged(prev, {
				width: terminal.columns,
				height: terminal.rows
			}));
			return;
		}
		if (ref.current === null) return;
		const measured = measureElement(ref.current);
		setSize((prev) => updateIfChanged(prev, measured));
	}, [
		ref,
		terminal.columns,
		terminal.rows,
		tick
	]);
	return size;
}
/**
* Compute the initial size from a ref or terminal dimensions.
*
* @private
* @param ref - Optional element ref.
* @param terminal - Current terminal dimensions.
* @returns The initial size.
*/
function resolveInitialSize(ref, terminal) {
	if (ref === void 0) return Object.freeze({
		width: terminal.columns,
		height: terminal.rows
	});
	if (ref.current === null) return Object.freeze({
		width: 0,
		height: 0
	});
	return Object.freeze(measureElement(ref.current));
}
/**
* Reducer that increments a tick counter. Used to force re-measurement
* after the ref is populated on mount.
*
* @private
* @param n - The current tick count.
* @returns The next tick count.
*/
function incrementTick(n) {
	return n + 1;
}
/**
* Return the next size only when dimensions have actually changed,
* preserving referential identity to avoid unnecessary re-renders.
*
* @private
* @param prev - The previous size.
* @param next - The candidate next size.
* @returns The previous size if unchanged, otherwise the next size (frozen).
*/
function updateIfChanged(prev, next) {
	if (prev.width === next.width && prev.height === next.height) return prev;
	return Object.freeze({
		width: next.width,
		height: next.height
	});
}
//#endregion
//#region src/screen/output/use-output-store.ts
/**
* Access the {@link OutputStore} attached to the current screen context.
*
* The store is attached by `screen()` via {@link injectOutputStore} and
* retrieved here via {@link getOutputStore}.
* Used internally by `<Output />` and available for advanced use cases
* that need direct store access.
*
* @returns The output store for the current screen.
*/
function useOutputStore() {
	return getOutputStore(useScreenContext());
}
//#endregion
//#region src/ui/output.tsx
/**
* Render accumulated output entries and spinner state from the screen's
* {@link OutputStore}.
*
* Automatically retrieves the store from the screen context provider.
* Subscribes via `useSyncExternalStore` and re-renders whenever new
* entries are pushed or spinner state changes.
*
* @returns A rendered output element.
*/
function Output() {
	const store = useOutputStore();
	const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
	return /* @__PURE__ */ jsxs(Box, {
		flexDirection: "column",
		children: [/* @__PURE__ */ jsx(SpinnerRow, { state: snapshot.spinner }), snapshot.entries.map((entry) => /* @__PURE__ */ jsx(EntryRow, { entry }, `${entry.kind}-${String(entry.id)}`))]
	});
}
/**
* Render the spinner row based on current spinner state.
*
* @private
*/
function SpinnerRow({ state }) {
	return match(state).with({ status: "idle" }, () => null).with({ status: "spinning" }, ({ message }) => /* @__PURE__ */ jsx(Spinner, { label: message })).with({ status: "stopped" }, ({ message }) => resolveTerminalIcon(message, colors.success, symbols.tick)).with({ status: "cancelled" }, ({ message }) => resolveTerminalIcon(message, colors.warning, symbols.warning)).with({ status: "error" }, ({ message }) => resolveTerminalIcon(message, colors.error, symbols.cross)).exhaustive();
}
/**
* Render a terminal-state spinner icon with a message, or null if the message is empty.
*
* @private
*/
function resolveTerminalIcon(message, color, icon) {
	if (message.length === 0) return null;
	return /* @__PURE__ */ jsxs(Text, { children: [
		/* @__PURE__ */ jsx(Text, {
			color,
			children: icon
		}),
		" ",
		message
	] });
}
/**
* Render a single output entry.
*
* @private
*/
function EntryRow({ entry }) {
	return match(entry).with({ kind: "log" }, (e) => /* @__PURE__ */ jsx(LogRow, {
		level: e.level,
		text: e.text,
		symbol: e.symbol
	})).with({ kind: "raw" }, (e) => /* @__PURE__ */ jsx(Text, { children: e.text })).with({ kind: "newline" }, () => /* @__PURE__ */ jsx(Text, { children: " " })).with({ kind: "check" }, (e) => /* @__PURE__ */ jsx(Text, { children: formatCheck(e.input) })).with({ kind: "finding" }, (e) => /* @__PURE__ */ jsx(Text, { children: formatFinding(e.input) })).with({ kind: "summary" }, (e) => /* @__PURE__ */ jsx(Text, { children: formatSummary(e.input) })).exhaustive();
}
/**
* Render a log entry with the appropriate color and icon.
*
* @private
*/
function LogRow({ level, text, symbol }) {
	return match(level).with("info", () => /* @__PURE__ */ jsxs(Text, { children: [
		/* @__PURE__ */ jsx(Text, {
			color: colors.info,
			children: symbols.circle
		}),
		" ",
		text
	] })).with("success", () => /* @__PURE__ */ jsxs(Text, { children: [
		/* @__PURE__ */ jsx(Text, {
			color: colors.success,
			children: symbols.tick
		}),
		" ",
		text
	] })).with("error", () => /* @__PURE__ */ jsxs(Text, { children: [
		/* @__PURE__ */ jsx(Text, {
			color: colors.error,
			children: symbols.cross
		}),
		" ",
		text
	] })).with("warn", () => /* @__PURE__ */ jsxs(Text, { children: [
		/* @__PURE__ */ jsx(Text, {
			color: colors.warning,
			children: symbols.warning
		}),
		" ",
		text
	] })).with("step", () => /* @__PURE__ */ jsxs(Text, { children: [
		/* @__PURE__ */ jsx(Text, {
			color: colors.primary,
			children: symbols.circle
		}),
		" ",
		text
	] })).with("message", () => /* @__PURE__ */ jsxs(Text, { children: [match(symbol).with(void 0, () => "").otherwise((s) => `${s} `), text] })).exhaustive();
}
//#endregion
//#region src/ui/keys.ts
/**
* Maps Ink `Key` boolean field names to normalized key name strings.
*
* @private
*/
const INK_KEY_ENTRIES = [
	["upArrow", "up"],
	["downArrow", "down"],
	["leftArrow", "left"],
	["rightArrow", "right"],
	["pageUp", "pageup"],
	["pageDown", "pagedown"],
	["home", "home"],
	["end", "end"],
	["return", "return"],
	["escape", "escape"],
	["tab", "tab"],
	["backspace", "backspace"],
	["delete", "delete"]
];
/**
* Modifier key names used as prefixes in key patterns (e.g. `ctrl+c`).
*
* @private
*/
const MODIFIER_NAMES = new Set([
	"ctrl",
	"meta",
	"shift"
]);
/**
* Normalize Ink's `(input, Key)` callback arguments into a single
* {@link NormalizedKeyEvent} with a resolved key name string.
*
* Priority: special keys (from `Key` booleans) take precedence over
* character input. Space is normalized to `'space'`.
*
* @param input - The raw character(s) from Ink's `useInput`.
* @param key - The Ink `Key` object with boolean flags.
* @returns A normalized key event.
*/
function normalizeKey(input, key) {
	const resolvedKey = match(resolveSpecialKey(key)).with(null, () => match(input).with(" ", () => "space").otherwise(() => input)).otherwise((name) => name);
	return {
		key: resolvedKey,
		ctrl: key.ctrl,
		meta: match(resolvedKey).with("escape", () => false).otherwise(() => key.meta),
		shift: key.shift
	};
}
/**
* Parse a declarative key pattern string into a structured
* {@link ParsedKeyPattern}. Supports single keys (`'q'`), modifier
* combinations (`'ctrl+c'`), and space-separated sequences
* (`'escape escape'`).
*
* @param pattern - The key pattern string to parse.
* @returns The parsed key pattern.
*/
function parseKeyPattern(pattern) {
	const parts = pattern.split(" ");
	if (parts.length > 1) return {
		type: "sequence",
		steps: parts.map(parseSinglePattern)
	};
	return parseSinglePattern(pattern);
}
/**
* Check whether a {@link NormalizedKeyEvent} matches a
* {@link SingleKeyPattern}. Compares the key name and all modifier flags.
*
* @param pattern - The parsed single-key pattern.
* @param event - The normalized key event to test.
* @returns `true` when the event matches the pattern.
*/
function matchesSingleKey(pattern, event) {
	return pattern.key === event.key && pattern.ctrl === event.ctrl && pattern.meta === event.meta && pattern.shift === event.shift;
}
/**
* Check whether a sequence of recent {@link NormalizedKeyEvent}s matches a
* {@link SequenceKeyPattern}. Compares the last N events (where N is the
* number of steps) and verifies all timestamps fall within the timeout.
*
* @param pattern - The parsed sequence key pattern.
* @param history - Recent key events with timestamps, newest last.
* @param timeout - Maximum elapsed time in ms between first and last event.
* @returns `true` when the history tail matches the full sequence within timeout.
*/
function matchesSequence(pattern, history, timeout) {
	const stepCount = pattern.steps.length;
	if (history.length < stepCount) return false;
	const startIndex = history.length - stepCount;
	const first = history[startIndex];
	const last = history.at(-1);
	if (first === void 0 || last === void 0) return false;
	if (last.timestamp - first.timestamp > timeout) return false;
	return pattern.steps.every((step, index) => {
		const event = history[startIndex + index];
		if (event === void 0) return false;
		return matchesSingleKey(step, event);
	});
}
/**
* Resolve the first active special key from an Ink `Key` object.
* Returns `null` when no special key boolean is `true`.
*
* @private
* @param key - The Ink Key object.
* @returns The normalized key name, or null.
*/
function resolveSpecialKey(key) {
	const found = INK_KEY_ENTRIES.find(([field]) => key[field] === true);
	if (found === void 0) return null;
	return found[1];
}
/**
* Parse a single key pattern segment (no spaces). Handles modifier
* prefixes like `ctrl+`, `meta+`, `shift+`.
*
* @private
* @param segment - A single key pattern segment.
* @returns The parsed single key pattern.
*/
function parseSinglePattern(segment) {
	const parts = segment.split("+");
	const modifierList = new Set(parts.slice(0, -1).filter((p) => MODIFIER_NAMES.has(p)));
	return {
		type: "single",
		key: parts.at(-1) ?? segment,
		ctrl: modifierList.has("ctrl"),
		meta: modifierList.has("meta"),
		shift: modifierList.has("shift")
	};
}
//#endregion
//#region src/ui/use-key-binding.ts
/**
* Default timeout in ms for multi-key sequences.
*/
const DEFAULT_SEQUENCE_TIMEOUT = 600;
/**
* Minimum key history entries retained for sequence matching.
*/
const MIN_HISTORY_LENGTH = 10;
/**
* Bind one or more key patterns to a single action. Supports single keys
* (`'q'`), modifier combinations (`'ctrl+c'`), and space-separated
* sequences (`'escape escape'`). Use multiple `useHotkey` calls for
* independent actions.
*
* @param args - Key patterns, action callback, and optional configuration.
* @returns Nothing.
*/
function useHotkey({ keys, action, active = true, sequenceTimeout = DEFAULT_SEQUENCE_TIMEOUT }) {
	const historyRef = useRef([]);
	const actionRef = useRef(action);
	const activeRef = useRef(active);
	const keysKey = keys.join("\0");
	useEffect(() => {
		actionRef.current = action;
	});
	activeRef.current = active;
	const parsedPatterns = useMemo(() => keys.map(parseKeyPattern), [keysKey]);
	const maxHistory = useMemo(() => resolveMaxHistory(parsedPatterns), [parsedPatterns]);
	useInput(useCallback((input, key) => {
		const normalized = normalizeKey(input, key);
		const entry = {
			...normalized,
			timestamp: Date.now()
		};
		historyRef.current = [...historyRef.current.slice(-(maxHistory - 1)), entry];
		if (!activeRef.current) return;
		if (parsedPatterns.some((pattern) => checkBinding(pattern, normalized, historyRef.current, sequenceTimeout))) actionRef.current();
	}, [
		parsedPatterns,
		sequenceTimeout,
		maxHistory
	]));
}
/**
* Derive the history buffer size from the longest sequence binding.
*
* Returns at least {@link MIN_HISTORY_LENGTH} so the buffer is never
* smaller than a reasonable default, even when no sequence bindings
* are registered.
*
* @private
* @param patterns - The parsed key patterns from all bindings.
* @returns The required history length.
*/
function resolveMaxHistory(patterns) {
	const longest = patterns.reduce((max, p) => Math.max(max, resolvePatternLength(p)), 0);
	return Math.max(longest, MIN_HISTORY_LENGTH);
}
/**
* Get the effective length of a parsed key pattern.
*
* @private
* @param pattern - The parsed key pattern.
* @returns The number of key events the pattern requires.
*/
function resolvePatternLength(pattern) {
	return match(pattern).with({ type: "single" }, () => 1).with({ type: "sequence" }, (p) => p.steps.length).exhaustive();
}
/**
* Check whether a normalized key event (and recent history) matches a
* parsed key pattern.
*
* @private
* @param pattern - The parsed key pattern to check.
* @param event - The current normalized key event.
* @param history - Recent key history entries with timestamps.
* @param timeout - Sequence timeout in ms.
* @returns `true` when the event matches the pattern.
*/
function checkBinding(pattern, event, history, timeout) {
	return match(pattern).with({ type: "single" }, (p) => matchesSingleKey(p, event)).with({ type: "sequence" }, (p) => matchesSequence(p, history, timeout)).exhaustive();
}
//#endregion
export { resolveVariantColor as _, Tabs as a, Select as c, ErrorMessage as d, Confirm as f, colors as g, CursorValue as h, useSize as i, resolveNextState as l, resolveInitialIndex as m, Output as n, Spinner as o, OptionRow as p, useOutputStore as r, TextInput as s, useHotkey as t, MultiSelect as u, symbols as v, ScrollArea as y };

//# sourceMappingURL=use-key-binding-Cwei5N22.js.map